import { Component, ViewEncapsulation, OnInit } from '@angular/core';
import { ModalController } from '@ionic/angular';

import { BookJob } from 'src/providers/book-job/book-job';
import { CurrentAddress } from 'src/providers/addresses/current-address';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { Client } from 'src/providers/client/client';
import { Pros } from 'src/providers/pros/pros';
import { Schedule } from 'src/providers/schedule/schedule';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { WindowService } from 'src/shared/providers/window.service';
import { BookService } from 'src/shared/services/book.service';
import { DemoBlockService } from 'src/shared/services/demo-block.service';
import { Util } from 'src/shared/util/util';

import { ConfirmPage, ConfirmPageParamsModel } from 'src/pages/confirm/confirm.component';
import { Loading } from 'src/shared/components/loading/loading';

@Component({
    templateUrl: 'plans.html',
    encapsulation: ViewEncapsulation.None,
    standalone: false
})

export class PlansPage implements OnInit {

  address: any;
  addressFilter: any;
  addressResponse: any;
  cameFromBookingFlow: boolean;
  categoryId: number;
  errorMessage: string;
  jobs: any;
  plans: any;
  isRightSideContent: boolean;
  loaded: boolean;

  constructor(
    private bookJobService: BookJob,
    private client: Client,
    private currentAddress: CurrentAddress,
    private modalCtrl: ModalController,
    private navCtrl: CustomNavController,
    private pros: Pros,
    private rightSidePanelService: RightSidePanelService,
    private schedule: Schedule,
    private storage: TidyStorage,
    private util: Util,
    private windowService: WindowService,
    private bookService: BookService,
    private demoBlock: DemoBlockService
  ) {}

  async ngOnInit() {
    this.loaded = false;
    this.isRightSideContent = await this.storage.retrieve('dialog-right-side-open') || false;
    if (this.isRightSideContent) {
      this.rightSidePanelService.setDialogPageTitle('Plans');
      const dialogParams = await this.storage.retrieve('dialog-params');
      this.plans = dialogParams?.plans;
      this.address = dialogParams?.address;
      this.addressFilter = dialogParams?.addressFilter;
      this.addressResponse = await this.client.getMoreDetailAddresses(true);
      this.address = this.address || this.resolveFallbackAddress();
      this.jobs = dialogParams?.cards || (this.address?.id ? await this.schedule.getCards(this.address.id) : []);
      this.cameFromBookingFlow = dialogParams?.cameFromBookingFlow;
      this.categoryId = dialogParams?.categoryId;
      if (this.address?.id) {
        await this.changeAddress(this.address.id, true);
      }
    } else {
      this.plans = this.navCtrl.getParam('plans', true);
      this.address = this.navCtrl.getParam('address', true);
      this.addressFilter = this.navCtrl.getParam('addressFilter', true);
      this.addressResponse = await this.client.getMoreDetailAddresses(true);
      this.address = this.address || this.resolveFallbackAddress();
      this.jobs = this.navCtrl.getParam('cards') || (this.address?.id ? await this.schedule.getCards(this.address.id) : []);
      this.cameFromBookingFlow = this.navCtrl.getParam('cameFromBookingFlow');
      this.categoryId = this.navCtrl.getParam('categoryId');
      if (this.address?.id) {
        await this.changeAddress(this.address.id, true);
      }
    }
    this.loaded = true;
  }

  private resolveFallbackAddress(): any {
    if (!this.addressResponse?.length) {
      return null;
    }
    const currentId = this.currentAddress.addressId;
    return this.addressResponse.find((a: any) => a?.id?.toString() === currentId) || this.addressResponse[0];
  }

  @Loading('', true)
  async changeAddress(selectedAddressId, isInitialLoad = false) {
    try {
      if (!isInitialLoad) {
        this.jobs = await this.schedule.getCards(selectedAddressId);
      }
      this.addressResponse.map((address) => {
        if (address.id === selectedAddressId) {
          this.plans = this.cameFromBookingFlow ? address.plans.filter((plan) => plan.service_type.id === this.categoryId) : address.plans;
          this.address = address;
          this.currentAddress.addressId = address.id.toString();
        };
      });
    } catch (err) {
      this.errorMessage = (err.error && err.error.message) ? err.error.message : err.message;
    }
  }

  getJobsFromPlan(planId) {
    const jobs: any = [];
    this.jobs.map((job) => {
      if (job.template_data?.booking?.plan_id === planId && job?.template === 'cleaning') {
        jobs.push(job);
      }
    });
    return jobs;
  }

  getJobRequestsFromPlan(planId) {
    const jobRequests: any = [];
    this.jobs.map((job) => {
      if (job.template_data?.booking?.plan_id === planId && job?.template === 'job_request') {
        jobRequests.push(job);
      }
    });
    return jobRequests;
  }

  async goToReschedulePlan(plan) {
    const params = {
      bookingType: 'reschedule_plan',
      address: this.address,
      planId: plan.id,
      isReschedulingOneTimePlan: plan.plan_frequency === 'once' ? 'job' : 'plan'
    }
    if (plan.is_private) {
      params['filterByPro'] = true;
      params['hkId'] = plan.team.owner.id;
    }
    const dialogParams = await this.storage.retrieve('dialog-params');
    params['categoryId'] = dialogParams?.categoryId;
    params['categoryHeader'] = dialogParams?.categoryHeader;
    await this.bookService.goToBookJob(params);
  }

  async goToAddOneTimeJob(plan) {
    await this.storage.save('bookJobBackPage', 'plans');
    const params = {
      address: this.address,
      bookingType: 'add_one_time_job',
      isReschedulingOneTimePlan: false
    }
    if (plan.is_private) {
      params['filterByPro'] = true;
      params['hkId'] = plan.team.owner.id;
    }
    const dialogParams = await this.storage.retrieve('dialog-params');
    params['categoryId'] = dialogParams?.categoryId;
    params['categoryHeader'] = dialogParams?.categoryHeader;
    await this.bookService.goToBookJob(params);
  }

  async cancelPlan(plan) {
    if (this.demoBlock.block()) {return;}
    const isOnce = plan?.plan_frequency === 'once';
    const hasMultipleJobsInPlan = (this.getJobsFromPlan(plan?.id)?.length + this.getJobRequestsFromPlan(plan?.id)?.length) > 1;
    const params: ConfirmPageParamsModel = {
      title: isOnce ? (hasMultipleJobsInPlan ? 'Cancel Jobs?' : 'Cancel Job?') : 'Cancel Plan?',
      body: isOnce ? 'This will cancel your scheduled job(s).' : 'This will cancel the plan and any pending jobs.',
      backText: 'Go Back',
      confirmText: isOnce ? (hasMultipleJobsInPlan ? 'Cancel Jobs' : 'Cancel Job') : 'Cancel Plan',
      confirmAction: this.confirmCancel.bind(this, plan.id, isOnce, hasMultipleJobsInPlan),
    };
    const confirmationModal = await this.modalCtrl.create({
      component: ConfirmPage,
      componentProps: params,
      animated: false,
      cssClass: 'confirm-modal'
    });
    await confirmationModal.present();
  }

  async confirmCancel(planId, isOnce, hasMultipleJobsInPlan) {
    try {
      this.storage.delete('moreDetailAddresses');
      await this.schedule.cancelPlan(planId);
      localStorage.setItem('madeUpdateToSchedule', 'true');
      const successMessage = isOnce ? (hasMultipleJobsInPlan ? 'Jobs cancelled' : 'Job cancelled') : 'Plan cancelled';
      this.util.showSuccess(successMessage);
      this.modalCtrl.dismiss();
      if (this.windowService.isDesktopRes) {
        this.rightSidePanelService.closeRightSidePanel();
      } else {
        const shouldShowAllToDosPage = localStorage.getItem('shouldShowAllToDosPage') === 'true';
        this.navCtrl.navigateForward(shouldShowAllToDosPage ? 'schedule' : 'schedule-list');
      }
    } catch (err) {
      this.errorMessage = (err.error && err.error.message) ? err.error.message : err.message;
      throw err;
    }
  }

  async bookJob(bookingType) {
    await this.storage.save('bookJobBackPage', 'plans');
    await this.client.getClientInfo();
    localStorage.setItem('region_id', this.address.region_id);
    const address = this.address;
    await this.bookService.goToBookJob({ address, bookingType });
  }

}
